'use client'

import {
  useAuth,
  authorizedFetch,
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
  Button,
  Input,
  Label,
  Textarea,
} from '@yeshuman/sdk'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import {
  useCreateNutritionMeal,
  useNutritionEstimatePhoto,
  useUpdateNutritionMeal,
  type MealLinePayload,
  type MealPayload,
} from '../hooks/use-nutrition'



export type MealEditorMode = 'create' | 'edit'

type LineDraft = {
  line_type: 'manual' | 'vision_estimated'
  name: string
  quantity: string
  unit: string
  energy_kcal: string
  protein_g: string
  carbohydrate_g: string
  fat_g: string
  fiber_g: string
  sugar_g: string
  sodium_mg: string
  vision_metadata?: Record<string, unknown>
}

function isoNowRounded(): string {
  return new Date().toISOString()
}

function emptyManualLine(): LineDraft {
  return {
    line_type: 'manual',
    name: '',
    quantity: '',
    unit: '',
    energy_kcal: '',
    protein_g: '',
    carbohydrate_g: '',
    fat_g: '',
    fiber_g: '',
    sugar_g: '',
    sodium_mg: '',
  }
}

function fromPayloadLines(lines: MealLinePayload[]): LineDraft[] {
  if (!lines.length) return []
  return lines.map((ln) => ({
    line_type: ln.line_type === 'vision_estimated' ? 'vision_estimated' : 'manual',
    name:
      ln.name ??
      (ln.line_type === 'vision_estimated'
        ? String((ln.vision_metadata as { name?: string } | undefined)?.name ?? '')
        : ''),
    quantity: ln.quantity ?? '',
    unit: ln.unit ?? '',
    energy_kcal: ln.energy_kcal ?? '',
    protein_g: ln.protein_g ?? '',
    carbohydrate_g: ln.carbohydrate_g ?? '',
    fat_g: ln.fat_g ?? '',
    fiber_g: ln.fiber_g ?? '',
    sugar_g: ln.sugar_g ?? '',
    sodium_mg: ln.sodium_mg ?? '',
    vision_metadata: ln.vision_metadata,
  }))
}

async function uploadFileToLibrary(file: File): Promise<{ id: string }> {
  const fd = new FormData()
  fd.append('file', file)
  const response = await authorizedFetch('/api/files/upload', { method: 'POST', body: fd })
  if (!response.ok) {
    const errText = await response.text().catch(() => '')
    throw new Error(errText || 'Upload failed')
  }
  const data = (await response.json()) as { id?: string }
  if (!data.id) throw new Error('Upload response missing id')
  return data as { id: string }
}

function linesToPayload(lines: LineDraft[]) {
  const prepared = lines.filter(
    (ln) => ln.line_type === 'vision_estimated' || (ln.line_type === 'manual' && ln.name.trim().length > 0)
  )
  return prepared.map((ln, idx) => {
    const macro = {
      energy_kcal: ln.energy_kcal || undefined,
      protein_g: ln.protein_g || undefined,
      carbohydrate_g: ln.carbohydrate_g || undefined,
      fat_g: ln.fat_g || undefined,
      fiber_g: ln.fiber_g || undefined,
      sugar_g: ln.sugar_g || undefined,
      sodium_mg: ln.sodium_mg || undefined,
    }
    if (ln.line_type === 'vision_estimated') {
      return {
        line_type: 'vision_estimated' as const,
        sort_order: idx,
        name: ln.name || String(ln.vision_metadata?.name ?? ''),
        vision_metadata: ln.vision_metadata ?? { name: ln.name },
        ...macro,
      }
    }
    return {
      line_type: 'manual' as const,
      sort_order: idx,
      name: ln.name,
      quantity: ln.quantity || undefined,
      unit: ln.unit || undefined,
      ...macro,
    }
  })
}

interface NutritionMealEditorProps {
  mode: MealEditorMode
  initialMeal?: MealPayload | null
  onClose: () => void
  onSaved: () => void
}

export function NutritionMealEditor({ mode, initialMeal, onClose, onSaved }: NutritionMealEditorProps) {
  const { token } = useAuth()
  const createM = useCreateNutritionMeal()
  const updateM = useUpdateNutritionMeal()
  const estimatePhoto = useNutritionEstimatePhoto()

  const [consumedAt, setConsumedAt] = useState(
    mode === 'edit' && initialMeal?.consumed_at ? initialMeal.consumed_at : isoNowRounded()
  )
  const [notes, setNotes] = useState(initialMeal?.notes ?? '')
  const [displayTitle, setDisplayTitle] = useState(initialMeal?.display_title ?? '')
  const [lines, setLines] = useState<LineDraft[]>(() =>
    mode === 'edit' && initialMeal ? fromPayloadLines(initialMeal.lines) : []
  )

  const hasVisionDisclaimer = useMemo(() => lines.some((l) => l.line_type === 'vision_estimated'), [lines])

  const baseline = useMemo(
    () => ({
      consumedAt: mode === 'edit' && initialMeal?.consumed_at ? initialMeal.consumed_at : '',
      notes: initialMeal?.notes ?? '',
      displayTitle: initialMeal?.display_title ?? '',
      lines: mode === 'edit' && initialMeal ? fromPayloadLines(initialMeal.lines) : [],
    }),
    [mode, initialMeal],
  )

  const isDirty =
    mode === 'create' ||
    consumedAt !== baseline.consumedAt ||
    notes !== baseline.notes ||
    displayTitle !== baseline.displayTitle ||
    JSON.stringify(lines) !== JSON.stringify(baseline.lines)

  const mutateUploading = createM.isPending || updateM.isPending || estimatePhoto.isPending || !token

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const addManualLine = () => setLines((prev) => [...prev, emptyManualLine()])

  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index))

  const save = async () => {
    const payloadLines = linesToPayload(lines)
    try {
      if (mode === 'create') {
        await createM.mutateAsync({
          consumed_at: consumedAt,
          notes,
          display_title: displayTitle,
          lines: payloadLines,
        })
        toast.success('Meal saved.')
      } else if (initialMeal) {
        await updateM.mutateAsync({
          mealId: initialMeal.id,
          body: {
            consumed_at: consumedAt,
            notes,
            display_title: displayTitle,
            lines: payloadLines,
          },
        })
        toast.success('Meal updated.')
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed.')
    }
  }

  const runEstimateFromFile = async (file: File) => {
    try {
      const upload = await uploadFileToLibrary(file)
      let mealId = initialMeal?.id
      if (!mealId) {
        const created = await createM.mutateAsync({
          consumed_at: consumedAt,
          notes,
          display_title: displayTitle || '',
          lines: [],
        })
        mealId = created.id
      }
      const result = await estimatePhoto.mutateAsync({
        mealId,
        fileAssetId: upload.id,
      })
      if (initialMeal) {
        toast.success(result?.vision_disclaimer ? 'Estimated items added.' : 'Photo processed.')
        onSaved()
        onClose()
      } else {
        toast.success(
          result?.vision_disclaimer ? 'Meal draft created — review estimates.' : 'Meal draft created.'
        )
        onSaved()
        onClose()
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Photo estimate failed.')
    }
  }

  const isSaving = createM.isPending || updateM.isPending

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
      <p className="text-xs text-muted-foreground">
        Macro totals are journaling aids only — visual estimates especially are approximate and not medical advice.
      </p>
      <div className="grid gap-2">
        <Label htmlFor="nut-title">Meal title (optional)</Label>
        <Input
          id="nut-title"
          value={displayTitle}
          onChange={(e) => setDisplayTitle(e.target.value)}
          placeholder="Breakfast, Lunch…"
          disabled={mutateUploading}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="nut-at">Consumed at (UTC / ISO)</Label>
        <Input id="nut-at" value={consumedAt} onChange={(e) => setConsumedAt(e.target.value)} disabled={mutateUploading} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="nut-notes">Notes</Label>
        <Textarea id="nut-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={mutateUploading} rows={4} />
      </div>

      {hasVisionDisclaimer ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
          Rows marked Vision are coarse photo-based guesses — verify before relying on macros.
        </p>
      ) : null}

      <div className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-medium">Food lines</h3>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={addManualLine} disabled={mutateUploading}>
              Add manual line
            </Button>
            <label className="inline-flex">
              <Button type="button" variant="outline" size="sm" asChild disabled={mutateUploading}>
                <span className="cursor-pointer">{estimatePhoto.isPending ? 'Estimating…' : 'Guess from photo'}</span>
              </Button>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={mutateUploading}
                onChange={(ev) => {
                  const f = ev.target.files?.[0]
                  ev.target.value = ''
                  if (f) void runEstimateFromFile(f)
                }}
              />
            </label>
          </div>
        </div>

        {lines.length === 0 ? (
          <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
            No food lines yet — add manual rows or guess from a photo.
          </p>
        ) : null}

        {lines.map((line, idx) => (
          <div key={idx} className="space-y-3 rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase text-muted-foreground">
                {line.line_type === 'vision_estimated' ? 'Vision-assisted' : 'Manual'}
              </span>
              <Button type="button" variant="ghost" size="sm" disabled={mutateUploading} onClick={() => removeLine(idx)}>
                Remove
              </Button>
            </div>
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={line.name}
                onChange={(e) => updateLine(idx, { name: e.target.value })}
                disabled={mutateUploading || line.line_type === 'vision_estimated'}
              />
            </div>
            {line.line_type === 'manual' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label>Qty</Label>
                  <Input
                    value={line.quantity}
                    onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                    disabled={mutateUploading}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Unit</Label>
                  <Input
                    value={line.unit}
                    onChange={(e) => updateLine(idx, { unit: e.target.value })}
                    disabled={mutateUploading}
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Macros are editable; food name stays from the estimate.</p>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(
                [
                  ['energy_kcal', 'kcal'],
                  ['protein_g', 'protein g'],
                  ['carbohydrate_g', 'carbs g'],
                  ['fat_g', 'fat g'],
                  ['fiber_g', 'fiber g'],
                  ['sugar_g', 'sugar g'],
                  ['sodium_mg', 'sodium mg'],
                ] as const
              ).map(([key, lbl]) => (
                <div key={key} className="grid gap-2">
                  <Label className="text-xs capitalize">{lbl}</Label>
                  <Input
                    value={String(line[key as keyof LineDraft] ?? '')}
                    onChange={(e) =>
                      updateLine(idx, {
                        [key]: e.target.value,
                      } as Partial<LineDraft>)
                    }
                    disabled={mutateUploading}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      </div>

      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={() => void save()}
            isDirty={isDirty}
            isSaving={isSaving}
            saveLabel={mode === 'create' ? 'Create meal' : 'Save changes'}
            savingLabel="Saving…"
            closeDisabled={estimatePhoto.isPending}
            saveDisabled={mutateUploading}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
